import React, {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {color, mb, textAlign, w100} from "@pg-design/helpers-css";
import {Input} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue, PhoneField} from "@pg-design/phone-input-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {usePostCreditApplicationMutation} from "../../application/api/post_application";
import {CreditApplicationSource} from "../../application/utils/CreditApplicationSource";
import {toJsonValues} from "../../form/translate/to_json_values";
import {validationMessages} from "../../form/utils/validation_messages";
import {addNotification} from "../../notifications/state/notifications_slice";
import {formFields, formValuesType, useCalculatorFormInitialValues} from "../hooks/use_calculator_form_initial_values";
import {ICreditData, ICreditRatingData} from "../types/ICreditData";

const validationSchema = Yup.object({
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    name: Yup.string().required(validationMessages.required),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
});

const credipassLink = "https://credipass.pl/polityka-prywatnosci/";

interface IProps {
    submitData: ICreditData | ICreditRatingData;
    onAfterSend: (isSuccess: boolean) => void;
    source: CreditApplicationSource;
}

export const CreditCalculatorForm = (props: IProps) => {
    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const initialValues = useCalculatorFormInitialValues();
    const rodoTermsUrl = rpAppLink.rodoTerms.base();

    const [post, data] = usePostCreditApplicationMutation();
    const [sending, setSending] = useState(false);

    const dispatch = useDispatch();
    const formMethods = useForm<formValuesType>({defaultValues: initialValues, resolver: yupResolver(validationSchema)});

    const {handleSubmit, control, formState, reset} = formMethods;
    const {errors} = formState;

    useEffect(() => {
        reset(initialValues);
    }, [initialValues]);

    useEffect(() => {
        if (data.status === "rejected") {
            const error = data.error as FetchBaseQueryError;
            notifyBugsnagClient({message: error.status.toString()}, "CreditCalculatorForm usePostCreditApplicationMutation error");
            dispatch(addNotification({type: "warning", content: "Twoje zapytanie nie zostało wysłane. Sprawdź podane dane lub spróbuj ponownie później."}));
            setSending(false);
        }

        if (data.status === "fulfilled") {
            props.onAfterSend(true);
        }
    }, [data]);

    const onSubmit = (values: formValuesType) => {
        setSending(true);
        const parsedValues = toJsonValues(formFields, values);
        const payload = {
            name: parsedValues.name,
            email: parsedValues.email,
            phone: parsedValues.phone,
            ...props.submitData
        };
        post(payload);
    };

    const renderError = (message?: string) => {
        return (
            <Text variant="info_txt_1" css={errorMessage}>
                {message}
            </Text>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
                name="name"
                control={control}
                render={({field}) => <Input placeholder="Imię i nazwisko" {...field} fieldState={errors.name && "error"} css={mb(1.5)} />}
            />
            {errors.name && renderError(errors.name.message)}
            <Controller
                name="email"
                control={control}
                render={({field}) => <Input placeholder="Email" {...field} fieldState={errors.email && "error"} css={mb(1.5)} />}
            />
            {errors.email && renderError(errors.email.message)}
            <div css={mb(1.5)}>
                <Controller
                    name="phone"
                    control={control}
                    render={({field: {onChange, value, name}}) => {
                        const onValueChange = (field: string, value: IPhoneFieldValue) => {
                            onChange(value);
                        };

                        return (
                            <PhoneField
                                codes={codes || []}
                                onChange={onValueChange}
                                onAfterChange={() => null}
                                value={value}
                                name={name}
                                placeholder="Numer telefonu"
                                fieldState={(errors.phone?.code || errors.phone?.number) && "error"}
                            />
                        );
                    }}
                />
            </div>
            {errors.phone?.code && renderError(errors.phone.code.message)}
            {errors.phone?.number && renderError(errors.phone.number.message)}

            <Button type="submit" variant="filled_primary" css={[w100, mb(1.5)]}>
                {sending ? <Loader size="sm" /> : "Wyślij"}
            </Button>

            <Text variant="info_txt_1" align="center">
                Administratorami Twoich danych osobowych są: Property Group sp. z o.o.
                <br />
                <Link size="x-small" underline href={rodoTermsUrl} target="_blank">
                    Dowiedz się więcej o przetwarzaniu Twoich danych.
                </Link>{" "}
                i Credipass
                <br />
                <Link size="x-small" underline href={credipassLink} target="_blank">
                    Dowiedz się więcej o przetwarzaniu Twoich danych.
                </Link>
            </Text>
        </form>
    );
};

const errorMessage = (theme: Theme) => css`
    ${color(theme.colors.danger)};
    ${w100};
    ${textAlign("right")};
    ${mb(1.5)};
`;
import React, {useEffect, useState} from "react";
import {Controller, useForm} from "react-hook-form";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme} from "@emotion/react";
import {yupResolver} from "@hookform/resolvers/yup";
import {FetchBaseQueryError} from "@reduxjs/toolkit/query";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {color, mb, textAlign, w100} from "@pg-design/helpers-css";
import {Input} from "@pg-design/inputs-module";
import {Link} from "@pg-design/link-module";
import {Loader} from "@pg-design/loader-module";
import {IPhoneFieldValue, PhoneField} from "@pg-design/phone-input-module";
import {Text} from "@pg-design/text-module";
import {notifyBugsnagClient} from "@pg-mono/bugsnag-client";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {usePostCreditApplicationMutation} from "../../application/api/post_application";
import {CreditApplicationSource} from "../../application/utils/CreditApplicationSource";
import {toJsonValues} from "../../form/translate/to_json_values";
import {validationMessages} from "../../form/utils/validation_messages";
import {addNotification} from "../../notifications/state/notifications_slice";
import {formFields, formValuesType, useCalculatorFormInitialValues} from "../hooks/use_calculator_form_initial_values";
import {ICreditData, ICreditRatingData} from "../types/ICreditData";

const validationSchema = Yup.object({
    email: Yup.string().required(validationMessages.required).email(validationMessages.email),
    name: Yup.string().required(validationMessages.required),
    phone: Yup.object({
        code: Yup.number().required(validationMessages.required),
        number: Yup.string().required(validationMessages.required)
    })
});

const credipassLink = "https://credipass.pl/polityka-prywatnosci/";

interface IProps {
    submitData: ICreditData | ICreditRatingData;
    onAfterSend: (isSuccess: boolean) => void;
    source: CreditApplicationSource;
}

export const CreditCalculatorForm = (props: IProps) => {
    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const initialValues = useCalculatorFormInitialValues();
    const rodoTermsUrl = rpAppLink.rodoTerms.base();

    const [post, data] = usePostCreditApplicationMutation();
    const [sending, setSending] = useState(false);

    const dispatch = useDispatch();
    const formMethods = useForm<formValuesType>({defaultValues: initialValues, resolver: yupResolver(validationSchema)});

    const {handleSubmit, control, formState, reset} = formMethods;
    const {errors} = formState;

    useEffect(() => {
        reset(initialValues);
    }, [initialValues]);

    useEffect(() => {
        if (data.status === "rejected") {
            const error = data.error as FetchBaseQueryError;
            notifyBugsnagClient({message: error.status.toString()}, "CreditCalculatorForm usePostCreditApplicationMutation error");
            dispatch(addNotification({type: "warning", content: "Twoje zapytanie nie zostało wysłane. Sprawdź podane dane lub spróbuj ponownie później."}));
            setSending(false);
        }

        if (data.status === "fulfilled") {
            props.onAfterSend(true);
        }
    }, [data]);

    const onSubmit = (values: formValuesType) => {
        setSending(true);
        const parsedValues = toJsonValues(formFields, values);
        const payload = {
            name: parsedValues.name,
            email: parsedValues.email,
            phone: parsedValues.phone,
            ...props.submitData
        };
        post(payload);
    };

    const renderError = (message?: string) => {
        return (
            <Text variant="info_txt_1" css={errorMessage}>
                {message}
            </Text>
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Controller
                name="name"
                control={control}
                render={({field}) => <Input placeholder="Imię i nazwisko" {...field} fieldState={errors.name && "error"} css={mb(1.5)} />}
            />
            {errors.name && renderError(errors.name.message)}
            <Controller
                name="email"
                control={control}
                render={({field}) => <Input placeholder="Email" {...field} fieldState={errors.email && "error"} css={mb(1.5)} />}
            />
            {errors.email && renderError(errors.email.message)}
            <div css={mb(1.5)}>
                <Controller
                    name="phone"
                    control={control}
                    render={({field: {onChange, value, name}}) => {
                        const onValueChange = (field: string, value: IPhoneFieldValue) => {
                            onChange(value);
                        };

                        return (
                            <PhoneField
                                codes={codes || []}
                                onChange={onValueChange}
                                onAfterChange={() => null}
                                value={value}
                                name={name}
                                placeholder="Numer telefonu"
                                fieldState={(errors.phone?.code || errors.phone?.number) && "error"}
                            />
                        );
                    }}
                />
            </div>
            {errors.phone?.code && renderError(errors.phone.code.message)}
            {errors.phone?.number && renderError(errors.phone.number.message)}

            <Button type="submit" variant="filled_primary" css={[w100, mb(1.5)]}>
                {sending ? <Loader size="sm" /> : "Wyślij"}
            </Button>

            <Text variant="info_txt_1" align="center">
                Administratorami Twoich danych osobowych są: Property Group sp. z o.o.
                <br />
                <Link size="x-small" underline href={rodoTermsUrl} target="_blank">
                    Dowiedz się więcej o przetwarzaniu Twoich danych.
                </Link>{" "}
                i Credipass
                <br />
                <Link size="x-small" underline href={credipassLink} target="_blank">
                    Dowiedz się więcej o przetwarzaniu Twoich danych.
                </Link>
            </Text>
        </form>
    );
};

const errorMessage = (theme: Theme) => css`
    ${color(theme.colors.danger)};
    ${w100};
    ${textAlign("right")};
    ${mb(1.5)};
`;
